import { Button } from "@simplicity/ui";
import { Plus, X } from "lucide-react";
import {
  MissingRequiredMessage,
  RequiredParamsEditor,
  ScheduleEntryExtraEditors,
} from "../tables/scheduleEntryEditors";
import { missingRequiredParams } from "../tables/scheduleEntryModel";
import type { ScheduleEntry, TableConfig } from "../tables/types";

/**
 * One table's options on one schedule (SIM-3402 slice 3). Writes go straight through,
 * matching the rest of the panel; there is no draft to cancel. Inline under its roster row
 * rather than in a side sheet, because a popover cannot hold `CadenceFiltersEditor`
 * (SIM-3515).
 */

export interface ScheduleTableOptionsProps {
  /** Labels the panel for assistive tech from the row's own toggle. */
  id: string;
  scheduleName: string;
  tableName: string;
  /** The table's `vars.tables` definition — undefined when the schedule names a table it has none for. */
  table: TableConfig | undefined;
  /** This table's entries on this schedule; more than one is the required-params case. */
  entries: ScheduleEntry[];
  /** Omit to render read-only, matching the roster's own editable rule. */
  onEntriesChange?: (next: ScheduleEntry[]) => void;
}

/**
 * A schedule may name a table `vars.tables` does not define. The runner fails that fetch on
 * every run with nothing to attribute it to, and the roster is the first surface that can
 * see both lists at once. SIM-3044, SIM-955.
 */
function OrphanWarning({ tableName }: { tableName: string }) {
  return (
    <div className="rounded-md border border-warning/40 bg-warning/10 p-2 text-xs" role="alert">
      <span className="font-medium">{tableName}</span> is on this schedule but has no definition in
      this connector&rsquo;s tables, so it cannot be fetched. Add it to Tables, or remove it from
      this schedule.
    </div>
  );
}

function InstanceHeading({
  instanceNumber,
  onRemove,
}: {
  instanceNumber: number | null;
  onRemove: (() => void) | undefined;
}) {
  if (instanceNumber === null && !onRemove) return null;
  return (
    <div className="mb-2 flex items-center justify-between">
      {instanceNumber !== null ? (
        <span className="text-xs font-medium text-muted-foreground">Instance {instanceNumber}</span>
      ) : (
        <span />
      )}
      {onRemove && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 hover:bg-destructive/20"
          onClick={onRemove}
          aria-label={`Remove instance ${instanceNumber ?? 1}`}
        >
          <X className="h-3 w-3" aria-hidden="true" />
        </Button>
      )}
    </div>
  );
}

function EntryCard({
  entry,
  instanceNumber,
  tableName,
  tableFields,
  requiredParams,
  onChange,
  onRemove,
}: {
  entry: ScheduleEntry;
  instanceNumber: number | null;
  tableName: string;
  tableFields: string[] | undefined;
  requiredParams: string[];
  onChange: (next: ScheduleEntry) => void;
  onRemove: (() => void) | undefined;
}) {
  const typedParams = (entry.params ?? {}) as Record<string, string | number>;
  const multiInstance = requiredParams.length > 0;

  return (
    <div className={multiInstance ? "rounded-md border border-border/60 bg-muted/20 p-2" : ""}>
      <InstanceHeading instanceNumber={instanceNumber} onRemove={onRemove} />
      {multiInstance && (
        <>
          <RequiredParamsEditor
            requiredParams={requiredParams}
            values={typedParams}
            onChange={(next) => onChange({ ...entry, params: next })}
          />
          <MissingRequiredMessage missing={missingRequiredParams(entry, requiredParams)} />
        </>
      )}
      <ScheduleEntryExtraEditors
        entry={entry}
        tableName={tableName}
        tableFields={tableFields}
        onChange={onChange}
      />
    </div>
  );
}

export function ScheduleTableOptions({
  id,
  scheduleName,
  tableName,
  table,
  entries,
  onEntriesChange,
}: ScheduleTableOptionsProps) {
  const editable = onEntriesChange !== undefined;
  const requiredParams = table?.requiredParams ?? [];
  const multiInstance = requiredParams.length > 0;

  // A membership with no entry cannot happen through the roster, but a hand-edited config can
  // reach it; editing the empty case beats rendering nothing.
  const shown = entries.length > 0 ? entries : [{ name: tableName }];

  function updateEntry(index: number, next: ScheduleEntry) {
    onEntriesChange?.(shown.map((entry, i) => (i === index ? next : entry)));
  }

  function removeInstance(index: number) {
    onEntriesChange?.(shown.filter((_, i) => i !== index));
  }

  return (
    // `disabled` on the wrapper rather than on each control: a read-only panel must not offer
    // a focusable Add or Remove either. A fieldset is also the tag form of `role="group"`.
    <fieldset
      id={id}
      disabled={!editable}
      className="mt-1 mb-2 rounded-md border border-border/60 bg-muted/20 p-3"
      aria-label={`Options for ${tableName} on ${scheduleName}`}
    >
      <p className="mb-2 text-xs text-muted-foreground">
        {editable
          ? `Applies on the ${scheduleName} schedule only.`
          : `Applies on the ${scheduleName} schedule only. Read-only.`}
      </p>

      <div className="space-y-3">
        {table === undefined && <OrphanWarning tableName={tableName} />}

        <div className="space-y-3">
          {shown.map((entry, index) => (
            <EntryCard
              // Instances share a name, so the index is the only stable identity they have.
              key={index}
              entry={entry}
              instanceNumber={multiInstance && shown.length > 1 ? index + 1 : null}
              tableName={tableName}
              tableFields={table?.fields}
              requiredParams={requiredParams}
              onChange={(next) => updateEntry(index, next)}
              onRemove={
                editable && multiInstance && shown.length > 1
                  ? () => removeInstance(index)
                  : undefined
              }
            />
          ))}

          {editable && multiInstance && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-1 text-xs text-muted-foreground"
              onClick={() => onEntriesChange?.([...shown, { name: tableName, params: {} }])}
            >
              <Plus className="h-3 w-3" aria-hidden="true" />
              Add another instance
            </Button>
          )}
        </div>
      </div>
    </fieldset>
  );
}
